import {
  Stack, Group, Text, Paper, Badge, Table, Progress,
  Alert, Loader, Center, Title, SimpleGrid,
} from '@mantine/core';
import { IconAlertCircle, IconClock, IconPointFilled } from '@tabler/icons-react';
import api from '@/config/api';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

interface AttendanceOccurrence {
  id: number;
  date: string;
  reason: string;
  points: number;
  expires_at: string;
}

interface AttendancePointsResponse {
  total_points: number;
  max_points: number;
  occurrences: AttendanceOccurrence[];
}

interface TimeEntry {
  id: number;
  date: string;
  clock_in: string;
  clock_out: string | null;
  regular_hours: number;
  ot_hours: number;
  break_minutes: number;
  status: string;
}

interface HoursResponse {
  entries: TimeEntry[];
  total_hours: number;
  total_ot: number;
}

function getScoreColor(total: number, max: number): string {
  if (max === 0) return 'green';
  const pct = total / max;
  if (pct < 0.5) return 'green';
  if (pct < 0.75) return 'yellow';
  return 'red';
}

function statusColor(status: string): string {
  switch (status) {
    case 'approved':
      return 'green';
    case 'pending':
      return 'yellow';
    case 'rejected':
      return 'red';
    case 'missed':
      return 'red';
    default:
      return 'gray';
  }
}

export default function MyAttendanceHistory() {
  const last30Start = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
  const today = dayjs().format('YYYY-MM-DD');

  // ---------- Queries ----------

  const pointsQuery = useQuery<AttendancePointsResponse>({
    queryKey: ['my-attendance-points'],
    queryFn: async () => {
      const res = await api.get('/time-attendance/my/attendance-points');
      return res.data;
    },
  });

  const hoursQuery = useQuery<HoursResponse>({
    queryKey: ['my-hours-history', last30Start, today],
    queryFn: async () => {
      const res = await api.get('/time-attendance/my/hours', {
        params: { start_date: last30Start, end_date: today },
      });
      return res.data;
    },
  });

  const totalPoints = pointsQuery.data?.total_points ?? 0;
  const maxPoints = pointsQuery.data?.max_points ?? 1;
  const occurrences = pointsQuery.data?.occurrences ?? [];
  const entries = hoursQuery.data?.entries ?? [];
  const scoreColor = getScoreColor(totalPoints, maxPoints);
  const scorePct = maxPoints > 0 ? (totalPoints / maxPoints) * 100 : 0;

  // ---------- Render ----------

  return (
    <Stack gap="lg">
      <Title order={2}>Attendance History</Title>

      {/* ===== Attendance Points Section ===== */}
      <Paper p="md" shadow="sm" radius="md" withBorder>
        <Group gap="xs" mb="md">
          <IconPointFilled size={20} />
          <Text fw={600} size="lg">Attendance Points</Text>
        </Group>

        {pointsQuery.isLoading ? (
          <Center h={150}><Loader /></Center>
        ) : pointsQuery.isError ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            Failed to load attendance points.
          </Alert>
        ) : (
          <Stack gap="md">
            {/* Points gauge */}
            <Paper p="md" radius="md" withBorder>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">Current Points</Text>
                  <Text size="xl" fw={700} c={scoreColor}>
                    {totalPoints} / {maxPoints}
                  </Text>
                  <Progress
                    value={scorePct}
                    color={scoreColor}
                    size="xl"
                    radius="xl"
                    style={{ width: '100%' }}
                  />
                  <Text size="xs" c="dimmed">
                    {totalPoints === 0
                      ? 'No attendance points - great job!'
                      : scorePct >= 75
                        ? 'Warning: Approaching maximum points threshold.'
                        : 'Points will expire over time.'}
                  </Text>
                </Stack>
                <Stack gap="xs" align="center">
                  <Text size="sm" c="dimmed">Total Occurrences</Text>
                  <Text size="xl" fw={700}>{occurrences.length}</Text>
                  <Text size="xs" c="dimmed">
                    {occurrences.length === 0
                      ? 'Clean record'
                      : `Earliest expires: ${occurrences.length > 0 ? dayjs(occurrences.reduce((a, b) => dayjs(a.expires_at).isBefore(dayjs(b.expires_at)) ? a : b).expires_at).format('MM/DD/YYYY') : 'N/A'}`}
                  </Text>
                </Stack>
              </SimpleGrid>
            </Paper>

            {/* Points history table */}
            {occurrences.length === 0 ? (
              <Text c="dimmed" ta="center" py="md">
                No attendance occurrences on record.
              </Text>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Reason</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Points</Table.Th>
                    <Table.Th>Expires At</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {occurrences.map((occ) => (
                    <Table.Tr key={occ.id}>
                      <Table.Td>{dayjs(occ.date).format('MM/DD/YYYY')}</Table.Td>
                      <Table.Td>{occ.reason}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Badge color="red" variant="light" size="sm">
                          +{occ.points}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{dayjs(occ.expires_at).format('MM/DD/YYYY')}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
        )}
      </Paper>

      {/* ===== Recent Time Entries Section ===== */}
      <Paper p="md" shadow="sm" radius="md" withBorder>
        <Group gap="xs" mb="md">
          <IconClock size={20} />
          <Text fw={600} size="lg">Recent Time Entries (Last 30 Days)</Text>
        </Group>

        {hoursQuery.isLoading ? (
          <Center h={150}><Loader /></Center>
        ) : hoursQuery.isError ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            Failed to load time entries.
          </Alert>
        ) : !entries.length ? (
          <Text c="dimmed" ta="center" py="md">
            No time entries found in the last 30 days.
          </Text>
        ) : (
          <Stack gap="sm">
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Clock In</Table.Th>
                  <Table.Th>Clock Out</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Regular Hours</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>OT Hours</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {entries.map((entry) => (
                  <Table.Tr key={entry.id}>
                    <Table.Td>{dayjs(entry.date).format('MM/DD/YYYY')}</Table.Td>
                    <Table.Td>{dayjs(entry.clock_in).format('h:mm A')}</Table.Td>
                    <Table.Td>
                      {entry.clock_out ? dayjs(entry.clock_out).format('h:mm A') : '-'}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {entry.regular_hours.toFixed(1)}
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      {entry.ot_hours.toFixed(1)}
                    </Table.Td>
                    <Table.Td>
                      <Badge color={statusColor(entry.status)} variant="light" size="sm">
                        {entry.status}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Group justify="flex-end">
              <Text size="sm" c="dimmed">
                Total: {hoursQuery.data?.total_hours?.toFixed(1) ?? '0.0'} regular,{' '}
                {hoursQuery.data?.total_ot?.toFixed(1) ?? '0.0'} OT hours
              </Text>
            </Group>
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}
